import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { closeOffCanvas, FormLayoutComponent, IFormLayout, IFormLayoutContent, IFormLayoutFooter, IFormLayoutHeader, IPolymorphicControlMeta, OffCanvasPlacement, OptimaOffCanvasService, PolymorphicControlService, PolyMorphicControlTypes, ToastService } from '@optima-stride-ai/optima-components';
import { IBizEntity, IBizEntityType, LoggerService } from '@optima-stride-ai/core';
import { BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { OffcanvasService } from '@coreui/angular';
import { GlobalStateService } from '../../../../../service/global.service';
import { take } from 'rxjs/operators';
import { createNewBizEntity } from '../biz-entity.factory';



@Component({
  selector: 'app-form-update-library-folder',
  imports: [FormLayoutComponent],
  templateUrl: './form-add-library.component.html',
  styleUrl: './form-add-library.component.sass',
})

export class FormUpdateLibraryComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly logger = inject(LoggerService);
  private isAPICallInProgress: boolean = false;

  constructor(private global: GlobalStateService) {
  }
  public updateBizEntityId: string | null = null;
  public bizEntity: any;
  public isLoading: boolean = true; // Add loading state
  async ngOnInit() {
    // Set initial loading state
    this.isLoading = true;

    // Get current values immediately to reduce delay
    this.global.updateBizEntity$.pipe(take(1)).subscribe(id => {
      this.updateBizEntityId = id;
    });

    this.global.isRenameClicked$.pipe(take(1)).subscribe(isRenamed => {
      this.isUpdateMode = isRenamed;
    });

    // Set up ongoing subscriptions
    this.global.updateBizEntity$.subscribe(async (id) => {
      this.updateBizEntityId = id;
      // Reload entity data when ID changes
      if (id) {
        await this.getBiz();
      }
    });

    this.global.isRenameClicked$.subscribe(async (isRenamed) => {
      if (isRenamed) {
        this.isUpdateMode = true;
      } else {
        this.isUpdateMode = false;
      }
      // Rebuild form when mode changes
      await this.buildFormLayout();
      this.cdr.detectChanges(); // Trigger change detection
    });

    // Load entity data first if we have an ID and are in update mode
    if (this.updateBizEntityId && this.isUpdateMode) {
      await this.getBiz();
    } else {
      // Initial form build for new entity
      await this.buildFormLayout();
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }
  formLayout!: IFormLayout;
  private libraryData!: IBizEntity;
  isUpdateMode: boolean = false;
  private readonly polymorphicControlService = inject(PolymorphicControlService);
  private readonly bizEntityStateStoreService = inject(BizEntityStateStoreService);
  private readonly bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private readonly toastService = inject(ToastService);
  private readonly offCanvasFormService = inject(OptimaOffCanvasService);
  private readonly offcanvasService = inject(OffcanvasService);




  private async buildFormLayout() {
    await this.initializeLibraryData();
    const layout: IFormLayout = {
      header: this.buildFormLayoutHeader(),
      content: this.buildFormLayoutContent(),
      footer: this.buildFormLayoutFooter(),
      cssClass: 'ben-form-library'
    };
    this.formLayout = layout;
    this.cdr.detectChanges(); // Ensure UI updates
  }

  private async initializeLibraryData(): Promise<void> {
    const bizEntityTypes = await this.bizEntityTypeStateStoreService.getState();
    const libraryType = bizEntityTypes?.find(type => type.name === 'Library') || (bizEntityTypes?.[0] || {} as IBizEntityType);
    
    this.libraryData = createNewBizEntity(
      libraryType,
      '', // parentId will be set dynamically
      'cilLibrary',
      ['Folder']
    );
  }

  public async getBiz(): Promise<void> {
    this.isLoading = true; // Set loading to true when fetching data
    const currentData = await this.bizEntityStateStoreService.getState();
    this.bizEntity = currentData?.find(item => item.id === this.updateBizEntityId);

    // Rebuild the form layout after loading the entity data
    if (this.bizEntity) {
      await this.buildFormLayout();
      this.cdr.detectChanges(); // Trigger change detection
    }
    this.isLoading = false; // Set loading to false after data is loaded
  }


  private buildFormLayoutHeader(): IFormLayoutHeader {
    return {
      title: this.isUpdateMode ? 'Update Library Folder' : 'Add Library Folder',
    };
  }

  private buildFormLayoutContent(): IFormLayoutContent {
    return {
      fieldItems: this.buildFormFields()
    };
  }

  private buildFormLayoutFooter(): IFormLayoutFooter {
    return {
      actions: [
        {
          label: 'Update Library Folder',
          icon: 'cilFolderOpen',
        },
      ]
    };
  }

  private populateLibraryFormWithValues(): IBizEntity {
    this.libraryData.name = this.formLayout.content?.fieldItems?.find(f => f.id === 'name')?.value || '';
    this.libraryData.description = this.formLayout.content?.fieldItems?.find(f => f.id === 'description')?.value || '';
    this.libraryData.icon = this.formLayout.content?.fieldItems?.find(f => f.id === 'icon')?.value || 'cilLibrary';
    this.libraryData.shortName = this.libraryData.name.substring(0, 50); // Limit short name
    // Note: parentId will be set in onCreateLibrary from selectedBizEntityId
    return this.libraryData;
  }

  get libraryBizEntityFormValues(): IBizEntity {
    return this.populateLibraryFormWithValues();
  }

  async onUpdateLibrary(): Promise<void> {
    // Prevent multiple rapid submissions
    if (this.isAPICallInProgress) {
      this.logger.warn('⚠️ API call already in progress, ignoring duplicate submission');
      return;
    }

    // Validate form fields before submission
    const name = this.formLayout.content?.fieldItems?.find(f => f.id === 'name')?.value?.trim();
    if (!name) {
      // Form is incomplete, don't submit
      return;
    }

    try {
      const values = this.populateLibraryFormWithValues();
      const updateEntity = {
        ...this.bizEntity,
        additionalInfo: this.bizEntity.additionalInfo ?? [],
        name: values.name,
        description: values.description,
        icon: values.icon,
      }
      
      // Set the flag and disable the submit button
      this.setAPICallInProgress(true);
      
      this.bizEntityStateStoreService.update(updateEntity, false)?.then((res) => {
        if (res) {
          this.afterSubmissionSucceeded(updateEntity);
        }
        this.setAPICallInProgress(false);
      }).catch((err) => {
        this.handleSubmissionError(err);
        this.logger.error(`Update error: ${err}`);
        this.setAPICallInProgress(false);
      });
    } catch (error) {
      this.handleSubmissionError(error);
      this.setAPICallInProgress(false);
    }
  }

  private afterSubmissionSucceeded(updatedEntity: any): void {
    closeOffCanvas(this.offCanvasFormService, this.offcanvasService, OffCanvasPlacement.End, true);
    this.toastService.toast('Library folder created successfully.');
    this.global.setRenameClicked(false);
    this.isUpdateMode = false;
    // Use the new setBizEntityUpdated instead of setBizEntityCreated, passing the updated entity
    this.global.setBizEntityUpdated(true, updatedEntity);
  }

  private setAPICallInProgress(inProgress: boolean): void {
    this.isAPICallInProgress = inProgress;
    // Disable/enable the submit button in the footer
    if (this.formLayout && this.formLayout.footer && this.formLayout.footer.actions.length > 0) {
      this.formLayout.footer.actions[0].isDisabled = inProgress;
    }
  }

  private handleSubmissionError(error: unknown): void {
    this.logger.error(`Error creating library folder: ${error}`);
    this.toastService.toast('Failed to create library folder. Please try again.');
  }

  private buildFormFields(): IPolymorphicControlMeta[] {
    const fields: IPolymorphicControlMeta[] = [];

    const libraryName = this.polymorphicControlService.getC({
      type: PolyMorphicControlTypes.text,
      label: 'Library Name',
      id: 'name',
      icon: 'cilLibrary',
      value: this.bizEntity ? this.bizEntity.name : '',
      placeholder: 'Enter library folder name'
    });

    const libraryDescription = this.polymorphicControlService.getC({
      type: PolyMorphicControlTypes.text,
      label: 'Description',
      id: 'description',
      icon: 'cilDescription',
      value: this.bizEntity ? this.bizEntity.description : '',
      placeholder: 'Enter library folder description (optional)'
    });

    const libraryIcon = this.polymorphicControlService.getC({
      type: PolyMorphicControlTypes.text,
      label: 'Icon',
      id: 'icon',
      icon: 'cilImagePlus',
      value: this.bizEntity ? this.bizEntity.icon : 'cilLibrary',
      placeholder: 'e.g., cilLibrary, cilFolder, cilFolderOpen'
    });

    fields.push(libraryName, libraryDescription, libraryIcon);
    return fields;
  }

}
